import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, RotateCw, Search, X } from 'lucide-react';
import { SortableHeader, type SortState } from '../ui/SortableHeader';
import { ColumnResizeHandle } from '../ui/ColumnResizeHandle';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { creditNotesApi } from '../../services/creditNotesApi';
import { formatDate } from '../../utils/format';
import { formatAmount, formatCount, formatCredited } from '../../utils/creditNoteFormat';
import {
    EMPTY_RECOURSE_FILTERS,
    creditNoteKey,
    type CreditNoteRecourseFilters,
    type CreditNoteRecourseQueue,
    type CreditNoteRecourseRow,
} from '../../types/creditNotes';

/**
 * The recourse queue — what we credited to a customer and still expect back from somebody else.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * THIS TAB IS NOT SCOPED BY THE OTHER TAB'S PERIOD, AND THAT IS THE POINT. A claim does not stop
 * being open because a reader narrowed the credit-note list to three months, and an open claim
 * on a fourteen-month-old credit note is exactly the one nobody should lose sight of. The
 * "Recourse open" KPI on the credit-notes tab counts the same set through the same server-side
 * function, so the tile and this tab cannot come to disagree.
 *
 * EVERY HEADLINE NUMBER HAS A CLICK THAT REPRODUCES IT. "Open claims" sets `openOnly`, "Overdue"
 * sets `overdueOnly`, and both read the same statuses and the same `isOverdue` the server
 * counted with. A number whose own filter returns something else is the failure this page exists
 * to expose in the ERP.
 * ────────────────────────────────────────────────────────────────────────────────────────────
 *
 * AMOUNTS ARE SHOWN PER CURRENCY AND NEVER SUMMED ACROSS THEM. A claim raised in CHF can be paid
 * in EUR — which is why every amount is stored with its own currency — and one blended
 * "expected" figure would be a number nobody could reconcile against anything.
 *
 * THE TABLE FOLLOWS THE HOUSE STANDARD without exception: sticky header, sortable, resizable.
 */

interface Col {
    id: string;
    label: string;
    sort?: SortKey;
    align?: 'left' | 'right';
    width: number;
}

type SortKey =
    'followUp' | 'company' | 'documentNo' | 'customer' | 'credited'
    | 'counterparty' | 'who' | 'expected' | 'received' | 'status';

const WIDTH_KEY = 'credit-notes-recourse-col-widths';

const COLS: Col[] = [
    { id: 'documentNo', label: 'Credit note', sort: 'documentNo', width: 130 },
    { id: 'company', label: 'Co.', sort: 'company', width: 62 },
    { id: 'customer', label: 'Customer', sort: 'customer', width: 210 },
    { id: 'credited', label: 'Credited EUR', sort: 'credited', align: 'right', width: 120 },
    { id: 'counterparty', label: 'Party', sort: 'counterparty', width: 96 },
    { id: 'who', label: 'Who', sort: 'who', width: 190 },
    { id: 'expected', label: 'Expected', sort: 'expected', align: 'right', width: 130 },
    { id: 'received', label: 'Received', sort: 'received', align: 'right', width: 130 },
    { id: 'followUp', label: 'Follow up', sort: 'followUp', width: 110 },
    { id: 'status', label: 'Status', sort: 'status', width: 130 },
];

/** Sorting is client-side here: the whole queue is a few dozen rows and arrives in one response. */
const SORTERS: Record<SortKey, (a: CreditNoteRecourseRow, b: CreditNoteRecourseRow) => number> = {
    // A case with no follow-up date sorts LAST, not first: an empty date is "nothing scheduled",
    // which must never read as "due long ago".
    followUp: (a, b) => (a.reviewDueAt ?? '9999-12-31').localeCompare(b.reviewDueAt ?? '9999-12-31'),
    company: (a, b) => a.companyCode.localeCompare(b.companyCode),
    documentNo: (a, b) => a.documentNo.localeCompare(b.documentNo),
    customer: (a, b) => (a.customerName ?? '').localeCompare(b.customerName ?? ''),
    credited: (a, b) => (a.creditedEur ?? 0) - (b.creditedEur ?? 0),
    counterparty: (a, b) => a.counterpartyType.localeCompare(b.counterpartyType),
    who: (a, b) => (a.counterpartyName ?? '').localeCompare(b.counterpartyName ?? ''),
    expected: (a, b) => (a.claimedAmount ?? 0) - (b.claimedAmount ?? 0),
    received: (a, b) => (a.receivedAmount ?? 0) - (b.receivedAmount ?? 0),
    status: (a, b) => a.status.localeCompare(b.status),
};

/** Per-currency totals, printed as "12'480.00 EUR · 3'200.00 CHF" rather than blended into one. */
function byCurrency(totals: Record<string, number>): string {
    const entries = Object.entries(totals);
    if (entries.length === 0) return '–';
    return entries
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([code, value]) => `${formatAmount(value)} ${code}`)
        .join(' · ');
}

interface Props {
    canView: boolean;
    /** Bumped by the drawer after a write, so the queue reloads instead of showing a stale case. */
    refreshToken: number;
    onOpenDocument: (companyCode: string, documentNo: string) => void;
}

export function RecourseTab({ canView, refreshToken, onOpenDocument }: Props) {
    const [data, setData] = useState<CreditNoteRecourseQueue | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<CreditNoteRecourseFilters>(EMPTY_RECOURSE_FILTERS);
    const [sortBy, setSortBy] = useState<SortKey>('followUp');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    const seq = useRef(0);

    const load = useCallback(async () => {
        if (!canView) return;
        const mine = ++seq.current;
        setLoading(true);
        setError(null);
        try {
            const result = await creditNotesApi.recourseQueue(filters);
            if (mine !== seq.current) return;
            setData(result);
        } catch (err) {
            if (mine !== seq.current) return;
            setError(err instanceof Error ? err.message : 'Failed to load the recourse queue');
        } finally {
            if (mine === seq.current) setLoading(false);
        }
    }, [canView, filters]);

    useEffect(() => { void load(); }, [load, refreshToken]);

    const { widthOf, totalWidthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(COLS.map(c => ({ key: c.id, width: c.width })), WIDTH_KEY);
    const totalWidth = totalWidthOf(COLS.map(c => c.id));

    const sort: SortState<SortKey> = { key: sortBy, direction: sortOrder };
    const onSort = (key: SortKey) => {
        if (shouldSuppressClick()) return;
        if (key === sortBy) setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
        else { setSortBy(key); setSortOrder(key === 'credited' || key === 'expected' || key === 'received' ? 'desc' : 'asc'); }
    };

    const summary = data?.summary;
    const rows = [...(data?.cases ?? [])].sort((a, b) => {
        const primary = SORTERS[sortBy](a, b) * (sortOrder === 'asc' ? 1 : -1);
        return primary !== 0 ? primary
            : a.companyCode.localeCompare(b.companyCode)
                || a.documentNo.localeCompare(b.documentNo)
                || a.caseNo - b.caseNo;
    });

    const activeFilters = [
        filters.search, filters.status, filters.counterpartyType, filters.company,
    ].filter(Boolean).length + (filters.overdueOnly ? 1 : 0) + (filters.openOnly ? 1 : 0);

    if (!canView) return null;

    return (
        <div className="space-y-4">
            {/* KPIs. Each of the two counting ones is also a filter, so its number can be
                reproduced by clicking it. */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <button
                    onClick={() => setFilters(f => ({ ...f, openOnly: !f.openOnly, status: '' }))}
                    className={`rounded-lg border px-3.5 py-3 text-left ${
                        filters.openOnly ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                >
                    <div className="text-xs text-gray-500">Open claims</div>
                    <div className="mt-0.5 text-[22px] font-semibold leading-7 text-gray-900">
                        {formatCount(summary?.openCases ?? 0)}
                    </div>
                    <div className="text-[11px] leading-4 text-gray-400">
                        {formatCount(summary?.openBySupplier ?? 0)} supplier ·{' '}
                        {formatCount(summary?.openByInsurance ?? 0)} insurance
                    </div>
                </button>

                <div className="rounded-lg border border-gray-200 bg-white px-3.5 py-3">
                    <div className="text-xs text-gray-500">Expected back</div>
                    <div className="mt-0.5 text-[17px] font-semibold leading-7 text-gray-900">
                        {summary ? byCurrency(summary.expectedByCurrency) : '–'}
                    </div>
                    <div className="text-[11px] leading-4 text-gray-400">
                        on the open claims · per currency, never added together
                    </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white px-3.5 py-3">
                    <div className="text-xs text-gray-500">Recovered</div>
                    <div className="mt-0.5 text-[17px] font-semibold leading-7 text-green-700">
                        {summary ? byCurrency(summary.receivedByCurrency) : '–'}
                    </div>
                    <div className="text-[11px] leading-4 text-gray-400">
                        {formatCount(summary?.settledCases ?? 0)} settled ·{' '}
                        {formatCount(summary?.notPursuedCases ?? 0)} written off
                    </div>
                </div>

                <button
                    onClick={() => setFilters(f => ({ ...f, overdueOnly: !f.overdueOnly }))}
                    className={`rounded-lg border px-3.5 py-3 text-left ${
                        filters.overdueOnly ? 'border-red-300 bg-red-50'
                            : (summary?.overdueCases ?? 0) > 0 ? 'border-red-200 bg-white hover:bg-red-50'
                                : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                >
                    <div className="text-xs text-gray-500">Overdue for follow-up</div>
                    <div className={`mt-0.5 text-[22px] font-semibold leading-7 ${
                        (summary?.overdueCases ?? 0) > 0 ? 'text-red-700' : 'text-gray-900'
                    }`}>
                        {formatCount(summary?.overdueCases ?? 0)}
                    </div>
                    <div className="text-[11px] leading-4 text-gray-400">
                        {summary?.oldestOverdueDays
                            ? `oldest ${summary.oldestOverdueDays} days past its date`
                            : 'nothing past its date'}
                    </div>
                </button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-end gap-3">
                <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-600">Search</span>
                    <span className="relative inline-block">
                        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                        <input
                            value={filters.search}
                            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                            placeholder="Credit note, customer, counterparty, ticket"
                            className="h-9 w-72 rounded border border-gray-300 pl-7 pr-2 text-sm"
                        />
                    </span>
                </label>

                <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-600">Status</span>
                    <select
                        value={filters.status}
                        onChange={e => setFilters(f => ({ ...f, status: e.target.value, openOnly: false }))}
                        className="h-9 w-44 rounded border border-gray-300 px-2 text-sm"
                    >
                        <option value="">Any status</option>
                        {(data?.vocabularies.recourseStatuses ?? []).map(v => (
                            <option key={v.code} value={v.code}>{v.label}</option>
                        ))}
                    </select>
                </label>

                <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-600">Counterparty</span>
                    <select
                        value={filters.counterpartyType}
                        onChange={e => setFilters(f => ({ ...f, counterpartyType: e.target.value }))}
                        className="h-9 w-40 rounded border border-gray-300 px-2 text-sm"
                    >
                        <option value="">Any</option>
                        {(data?.vocabularies.counterpartyTypes ?? []).map(v => (
                            <option key={v.code} value={v.code}>{v.label}</option>
                        ))}
                    </select>
                </label>

                <label className="block">
                    <span className="mb-1 block text-xs font-medium text-gray-600">Company</span>
                    <select
                        value={filters.company}
                        onChange={e => setFilters(f => ({ ...f, company: e.target.value }))}
                        className="h-9 w-40 rounded border border-gray-300 px-2 text-sm"
                    >
                        <option value="">All companies</option>
                        {(data?.availableCompanies ?? []).map(c => (
                            <option key={c} value={c}>
                                {c}{summary?.byCompany?.[c] !== undefined ? ` (${formatCount(summary.byCompany[c])})` : ''}
                            </option>
                        ))}
                    </select>
                </label>

                {activeFilters > 0 && (
                    <button
                        onClick={() => setFilters(EMPTY_RECOURSE_FILTERS)}
                        className="inline-flex h-9 items-center gap-1 rounded border border-gray-300 px-3 text-sm text-gray-600 hover:bg-gray-50"
                    >
                        <X className="h-4 w-4" /> Clear filters ({activeFilters})
                    </button>
                )}

                <span className="flex-1" />

                <button
                    onClick={() => void load()}
                    disabled={loading}
                    className="inline-flex h-9 items-center gap-2 rounded border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                    <RotateCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    Reload
                </button>
            </div>

            {error && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
            )}

            {/* Cases whose credit note is older than the server's enrichment window: listed,
                with empty ERP columns, and NAMED here. A queue that silently hides its oldest
                claim is worse than one that shows it incompletely. */}
            {summary && summary.outsideEnrichmentWindow.cases > 0 && (
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
                    {formatCount(summary.outsideEnrichmentWindow.cases)} case
                    {summary.outsideEnrichmentWindow.cases === 1 ? ' is' : 's are'} on a credit note older
                    than {summary.outsideEnrichmentWindow.windowMonths} months, so the customer and the
                    credited amount are not shown for{' '}
                    <span className="font-mono">{summary.outsideEnrichmentWindow.documentNos.join(', ')}</span>.
                    The case itself is in the list below and is counted in every figure above.
                </div>
            )}

            <div className="rounded-lg border border-gray-200 bg-white">
                <div className="max-h-[70vh] overflow-auto">
                    <table className="w-full table-fixed text-sm" style={{ minWidth: totalWidth }}>
                        <colgroup>
                            {COLS.map(c => <col key={c.id} style={{ width: widthOf(c.id) }} />)}
                        </colgroup>
                        <thead>
                            <tr>
                                {COLS.map(c => (c.sort ? (
                                    <SortableHeader
                                        key={c.id}
                                        label={c.label}
                                        sortKey={c.sort}
                                        sort={sort}
                                        onSort={onSort}
                                        align={c.align === 'right' ? 'right' : 'left'}
                                        className="shadow-[inset_0_-1px_0_rgb(229,231,235)]"
                                        onResizeStart={(x) => startResize(c.id, x)}
                                        onResetWidth={() => resetWidth(c.id)}
                                        isResizing={resizingKey === c.id}
                                    />
                                ) : (
                                    <th
                                        key={c.id}
                                        className="sticky top-0 z-10 relative bg-gray-50 px-3 py-2 text-left text-xs font-medium text-gray-500 shadow-[inset_0_-1px_0_rgb(229,231,235)]"
                                    >
                                        <span className="truncate">{c.label}</span>
                                        <ColumnResizeHandle
                                            label={c.label || c.id}
                                            active={resizingKey === c.id}
                                            onStart={(x) => startResize(c.id, x)}
                                            onReset={() => resetWidth(c.id)}
                                        />
                                    </th>
                                )))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && rows.length === 0 && (
                                <tr><td colSpan={COLS.length} className="px-3 py-16 text-center">
                                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-500" />
                                </td></tr>
                            )}
                            {!loading && rows.length === 0 && (
                                <tr><td colSpan={COLS.length} className="px-3 py-16 text-center text-sm text-gray-500">
                                    {activeFilters > 0
                                        ? 'No recourse case matches these filters.'
                                        : 'No recourse case has been opened yet. Open one from a credit note’s detail drawer.'}
                                </td></tr>
                            )}
                            {rows.map(r => (
                                <tr
                                    key={`${r.companyCode}-${r.documentNo}-${r.caseNo}`}
                                    onClick={() => onOpenDocument(r.companyCode, r.documentNo)}
                                    className={`cursor-pointer border-b border-gray-100 hover:bg-gray-50 ${
                                        r.isOverdue ? 'bg-red-50/60' : ''
                                    }`}
                                    title={`Open ${creditNoteKey(r.companyCode, r.documentNo)}`}
                                >
                                    <td className="truncate px-3 py-2 font-mono text-xs text-blue-700">{r.documentNo}</td>
                                    <td className="px-3 py-2 font-medium text-gray-600">{r.companyCode}</td>
                                    <td className="truncate px-3 py-2 text-gray-700" title={r.customerName ?? undefined}>
                                        {r.customerName ?? (
                                            <span className="text-gray-400">
                                                {r.documentInWindow ? 'no customer on the header' : 'outside the window'}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-red-700">
                                        {r.creditedEur === null ? <span className="text-gray-300">–</span> : formatCredited(r.creditedEur)}
                                    </td>
                                    <td className="truncate px-3 py-2 text-gray-600">
                                        {(data?.vocabularies.counterpartyTypes ?? [])
                                            .find(t => t.code === r.counterpartyType)?.label ?? r.counterpartyType}
                                    </td>
                                    <td className="truncate px-3 py-2 text-gray-700" title={r.counterpartyName ?? undefined}>
                                        {r.counterpartyName ?? <span className="text-gray-400">not named</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                                        {r.claimedAmount === null ? <span className="text-gray-300">–</span>
                                            : `${formatAmount(r.claimedAmount)} ${r.claimedCurrency ?? ''}`}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {r.receivedAmount === null ? <span className="text-gray-300">–</span>
                                            : <span className="text-green-700">{formatAmount(r.receivedAmount)} {r.receivedCurrency ?? ''}</span>}
                                    </td>
                                    <td className={`truncate px-3 py-2 ${r.isOverdue ? 'font-medium text-red-700' : 'text-gray-700'}`}>
                                        {r.reviewDueAt ? formatDate(r.reviewDueAt) : <span className="text-gray-300">–</span>}
                                    </td>
                                    <td className="truncate px-3 py-2">
                                        <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                                            r.isOverdue ? 'bg-red-50 text-red-700'
                                                : r.status === 'SETTLED' ? 'bg-green-50 text-green-700'
                                                    : r.status === 'NOT_PURSUED' || r.status === 'REJECTED'
                                                        ? 'bg-gray-100 text-gray-600'
                                                        : 'bg-blue-50 text-blue-700'
                                        }`}>
                                            {r.isOverdue
                                                ? `${r.daysOverdue} d overdue`
                                                : (data?.vocabularies.recourseStatuses ?? [])
                                                    .find(v => v.code === r.status)?.label ?? r.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <p className="text-[11px] leading-4 text-gray-400">
                The credit note, the company, the customer and the credited amount come from the ERP.
                Everything to the right of them — the counterparty, who, expected, received, follow-up
                and status — exists in no system Dolder runs, so this app keeps it, with a required
                reason and a full trail on every change. A row turns red once its follow-up date has
                passed and the claim is still open; open the credit note to see its trail.
                This tab is not narrowed by the period on the credit-notes tab: a claim does not stop
                being open because a date filter moved.
            </p>
        </div>
    );
}
